import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

const iconWrapperVariants = cva("relative rounded-lg", {
  variants: {
    variant: {
      default: "bg-background",
      frosted: "bg-transparent",
    },
    size: {
      sm: "size-8 [&>*:last-child>svg]:size-4",
      md: "size-10 [&>*:last-child>svg]:size-5",
      lg: "size-12 [&>*:last-child>svg]:size-6",
      xl: "size-16 [&>*:last-child>svg]:size-8",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const borderVariants = cva(
  "absolute inset-0 pointer-events-none rounded-lg shadow-sm",
  {
    variants: {
      state: {
        default: "border-2 border-solid border-border",
        active: "bg-gradient-border p-0.5",
        frosted: "border-2 border-solid border-border",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

const iconVariants = cva("text-white flex items-center justify-center", {
  variants: {
    state: {
      default: "",
      active: "[&_svg]:stroke-[url(#icon-gradient)] text-transparent",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export interface IconWrapperProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof borderVariants>,
    VariantProps<typeof iconWrapperVariants> {
  children?: React.ReactNode;
}

function IconWrapper({
  className,
  state = "default",
  variant = "default",
  size = "md",
  children,
  ...props
}: IconWrapperProps) {
  return (
    <div
      className={cn(iconWrapperVariants({ variant, size }), className)}
      {...props}
    >
      {variant === "frosted" && (
        <div className="absolute inset-0 backdrop-blur-3xl backdrop-filter rounded-lg">
          <div className="absolute bg-background inset-0 opacity-45 rounded-lg" />
        </div>
      )}

      <div
        className={borderVariants({
          state: variant === "frosted" ? "frosted" : state,
        })}
      >
        {state === "active" && variant !== "frosted" && (
          <div className="bg-background size-full rounded-md" />
        )}
      </div>

      {state === "active" && (
        <svg width="0" height="0" className="absolute" aria-hidden="true">
          <defs>
            <linearGradient
              id="icon-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="var(--gradient-from)" />
              <stop offset="100%" stopColor="var(--gradient-to)" />
            </linearGradient>
          </defs>
        </svg>
      )}

      <div
        className={cn(
          "absolute inset-0",
          iconVariants({
            state:
              state === "active" && variant !== "frosted"
                ? "active"
                : "default",
          }),
        )}
      >
        {children}
      </div>
    </div>
  );
}

export { IconWrapper, iconWrapperVariants, iconVariants };
